import { useEffect, useRef, useState } from 'react';
import { apiGet, apiPost, setToken } from './api.js';
import { tryShortcut } from './shortcuts.js';

// Pairing UI:
//   1. User types a 6-digit code (server-issued from /screens/pair on the manager)
//   2. We GET /screens/pair/:code → confirm the code is real, show business + branch
//   3. User confirms → POST /screens/pair/:code/claim → store token, hand off to player
//
// The input is auto-focused and accepts only digits. On a TV, the soft
// keyboard only shows numbers because of inputMode="numeric".

export default function Pair({ onPaired }) {
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState('input');   // 'input' | 'confirm' | 'claiming' | 'error'
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (phase === 'input') inputRef.current?.focus();
  }, [phase]);

  async function lookup(c) {
    setError(null);
    setPhase('input');
    try {
      const data = await apiGet(`/screens/pair/${c}`);
      setDetails(data);
      setPhase('confirm');
    } catch (err) {
      setError(err.body?.error || 'Code not found or expired.');
      setPhase('error');
    }
  }

  async function claim() {
    setPhase('claiming');
    try {
      const data = await apiPost(`/screens/pair/${code}/claim`, {
        userAgent: navigator.userAgent,
        appVersion: import.meta.env?.VITE_APP_VERSION || 'dev',
      });
      setToken(data.token);
      onPaired(data.screen);
    } catch (err) {
      setError(err.body?.error || 'Pairing failed. Try again.');
      setPhase('error');
    }
  }

  function handleCodeChange(value) {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6) {
      // Custom-customer shortcut codes redirect straight to a static menu URL
      // and never hit the backend pairing API.
      if (tryShortcut(digits)) return;
      lookup(digits);
    }
  }

  function reset() {
    setCode('');
    setDetails(null);
    setError(null);
    setPhase('input');
  }

  return (
    <div style={S.root}>
      <div style={S.brand}>
        <span style={S.brandMark}>▶ OPSORA</span>
        <span style={S.brandTag}>SCREENS</span>
      </div>

      {phase === 'input' && (
        <>
          <h1 style={S.h1}>Pair this screen</h1>
          <p style={S.subtitle}>
            Open <span style={S.kbd}>dashboard.opsora.us</span> on your phone or laptop, tap{' '}
            <span style={S.kbd}>Screens → Pair</span>, then enter the code below.
          </p>
          <input
            ref={inputRef}
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            inputMode="numeric"
            pattern="\d*"
            placeholder="000000"
            style={S.codeInput}
            maxLength={6}
            autoComplete="one-time-code"
          />
        </>
      )}

      {phase === 'confirm' && details && (
        <>
          <h1 style={S.h1}>Pair with {details.businessName}?</h1>
          <p style={S.subtitle}>
            Location: <strong>{details.branchName}</strong>
            {details.presetName && <> · Will be named “{details.presetName}”</>}
          </p>
          <div style={S.btnRow}>
            <button style={S.btnSecondary} onClick={reset}>Cancel</button>
            <button style={S.btnPrimary} onClick={claim}>Confirm pairing</button>
          </div>
        </>
      )}

      {phase === 'claiming' && (
        <>
          <h1 style={S.h1}>Pairing…</h1>
          <p style={S.subtitle}>One moment.</p>
        </>
      )}

      {phase === 'error' && (
        <>
          <h1 style={S.h1}>Couldn't pair</h1>
          <p style={{ ...S.subtitle, color: '#fb7185' }}>{error}</p>
          <div style={S.btnRow}>
            <button style={S.btnPrimary} onClick={reset}>Try again</button>
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  root: {
    width: '100%', height: '100%',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '4vh 4vw',
    background: 'radial-gradient(ellipse at top, #130f35 0%, #08061a 60%)',
  },
  brand: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: '6vh' },
  brandMark: {
    fontFamily: "'Space Mono', monospace",
    fontSize: 16, color: '#7c3aed', letterSpacing: 4, textTransform: 'uppercase',
  },
  brandTag: {
    background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)',
    borderRadius: 4, padding: '4px 10px',
    fontFamily: "'Space Mono', monospace", fontSize: 11, color: '#a78bfa',
    letterSpacing: 2,
  },
  h1: {
    fontSize: 'clamp(28px, 4vw, 56px)',
    fontWeight: 800, color: '#fff', textAlign: 'center',
    margin: '0 0 16px', letterSpacing: -0.5,
  },
  subtitle: {
    fontSize: 'clamp(14px, 1.4vw, 20px)',
    color: '#9ca3af', textAlign: 'center',
    margin: '0 0 4vh', lineHeight: 1.6, maxWidth: '80ch',
  },
  kbd: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 4, padding: '2px 8px',
    fontFamily: "'Space Mono', monospace", fontSize: '0.95em',
    color: '#e2e8f0',
  },
  codeInput: {
    fontFamily: "'Space Mono', monospace",
    fontSize: 'clamp(48px, 8vw, 120px)',
    fontWeight: 700, letterSpacing: '0.4em',
    width: '8em',
    padding: '12px 0', textAlign: 'center',
    background: 'rgba(255,255,255,0.05)',
    border: '2px solid rgba(124,58,237,0.4)',
    borderRadius: 16,
    color: '#fff',
    outline: 'none',
  },
  btnRow: { display: 'flex', gap: 16 },
  btnPrimary: {
    background: '#7c3aed', border: 'none', borderRadius: 12,
    padding: '14px 32px', fontSize: 18, fontWeight: 600, color: '#fff',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  btnSecondary: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12,
    padding: '14px 32px', fontSize: 18, fontWeight: 600, color: '#9ca3af',
    cursor: 'pointer', fontFamily: 'inherit',
  },
};
